import { Deprecation } from 'deprecation';
import { BaseImageType } from '../atoms/Image/image';
import {
  getFlatCamelCaseMap,
  cssColorVar,
  SemanticColor,
} from '@spotify-internal/encore-web';

import xss from 'xss';
import type { MarketContextValue } from '../../configuration/context/market';

export const responsiveSize = (
  minSize: number,
  maxSize: number,
  minViewport: number,
  maxViewport: number,
) =>
  `clamp(${minSize}px, calc(${minSize}px + (${maxSize} - ${minSize}) * ((100vw - ${minViewport}px) / (${maxViewport} - ${minViewport}))), ${maxSize}px)`;

export const deprecateImageField = ({
  oldProp,
  newProp,
  smallValue,
  mediumValue,
  largeValue,
}: {
  oldProp: string;
  newProp: string;
  smallValue: string;
  mediumValue?: string;
  largeValue?: string;
}): BaseImageType['sources'] => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      new Deprecation(
        `"${oldProp}" field is deprecated, please use "${newProp}" instead: https://sites.spotify.net/spa/%40spotify-internal-landing-page-library/?path=/docs/benefits--basic-benefits#v750`,
      ),
    );
  }

  const migratedObject: BaseImageType['sources'] = {
    small: { '1x': smallValue },
  };
  if (mediumValue) migratedObject.medium = { '1x': mediumValue };
  if (largeValue) migratedObject.large = { '1x': largeValue };

  return migratedObject;
};

export const purify = (input?: string | null) => {
  const options = {
    // eslint-disable-next-line
    whiteList: {
      a: ['class', 'style', 'href'],
      b: [], // deprecated, for retro compatibility only (use strong)
      br: [],
      div: ['class', 'style'], // deprecated, for retro compatibility only
      em: [],
      i: [], // deprecated, for retro compatibility only (use em)
      li: [],
      ol: [],
      p: [],
      span: ['class', 'style'], // deprecated, for retro compatibility only
      strong: [],
      sup: [],
      ul: [],
    },
  };

  return typeof input !== 'string' ? '' : xss(input, options);
};

export const isArabicMarket = (
  market: MarketContextValue,
  direction: string,
) => {
  return (
    market?.country && market?.country.includes('-ar') && direction === 'rtl'
  );
};

export const printThemeVars = ({ values }: { values: object }) => {
  const output = [];

  const themeProps = getFlatCamelCaseMap(values);
  for (const [key, value] of Object.entries(themeProps)) {
    output.push(`${cssColorVar(key as SemanticColor)}: ${value}`);
  }

  return `${output.join(';')};`;
};

export const hashCode = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash;
  }
  return new Uint32Array([hash])[0].toString(8);
};
